import { Grid } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useAchievements } from "api/achievements";
import {
  hasAnyAccessToResource,
  hasModifyAccessToResource,
  hasViewAccessToResource,
} from "api/general";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderMenuButton } from "components/Header/Subheader/SubheaderMenuButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { Breadcrumbs, TopHeader } from "components/Header/TopHeader/TopHeader";
import { StatusValue } from "components/Table/StatusValue";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { links } from "utilities/links";
import { BadgeNameChanger } from "../Badges/BadgeNameChanger";
import { BasicSettings } from "./BasicSettings";
import { Limit } from "./Limit";
import { Rules } from "./Rules";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    sectionHeader: {
      fontSize: 18,
      paddingBottom: theme.spacing(8),
    },
    gridContainer: {
      margin: theme.spacing(12, 0),
    },
    gridChild: {
      display: "flex",
      flexDirection: "column",
    },
  }),
);

export const SingleAchievement = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { message } = useMessage();
  const [editBadge, setEditBadge] = useState<{
    isOpen: boolean;
    badgeTypeId?: string;
  }>({
    isOpen: false,
    badgeTypeId: undefined,
  });
  const hasViewCampaignPermission = hasViewAccessToResource("CAMPAIGN");
  const hasAnyLanguagePermission = hasAnyAccessToResource("LANGUAGE");
  const hasModifyBadgePermission = hasModifyAccessToResource("BADGE");
  const { id: achievementId } = useParams<{ id: string }>();
  const { useAchievementData } = useAchievements();
  const { data: achievementData } = useAchievementData(achievementId);
  const breadcrumbs: Breadcrumbs = {
    parent: {
      label: message({ id: "listOfAchievements" }),
      path: links.achievementsList,
    },
  };
  const [achievementName, setAchievementName] = useState<string | undefined>();

  return (
    <>
      <TopHeader
        badge={<StatusValue status={!!achievementData?.active} />}
        breadcrumbs={breadcrumbs}
        clearLink={links.achievementsList}
        title={achievementName || ""}
      />
      <Subheader>
        <SubheaderWrapper alignRight>
          <SubheaderMenuButton
            text={message({ id: "actions" })}
            menuItems={[
              {
                onClick: () =>
                  navigate(links.editAchievement(achievementId as string)),
                hideCondition:
                  hasViewCampaignPermission || !hasAnyLanguagePermission,
                text: message({ id: "editAchievement" }),
              },
              {
                onClick: () =>
                  navigate(links.addAchievement, {
                    state: {
                      duplicateAchievementId: achievementId,
                    },
                  }),
                hideCondition:
                  hasViewCampaignPermission || !hasAnyLanguagePermission,
                text: message({ id: "duplicateAchievement" }),
              },
              {
                onClick: () =>
                  setEditBadge({
                    isOpen: true,
                    badgeTypeId: achievementData?.badgeTypeId,
                  }),
                hideCondition: !hasModifyBadgePermission,
                text: message({ id: "changeBadgeName" }),
              },
            ]}
            data-test-id="single-achievement"
          />
        </SubheaderWrapper>
      </Subheader>
      <Grid container className={classes.gridContainer}>
        <Grid className={classes.gridChild} item xs={12}>
          <span className={classes.sectionHeader}>
            {message({ id: "achievementDetails" })}
          </span>
          <BasicSettings
            achievementData={{
              translations: achievementData?.translations,
              activity: achievementData?.activity,
              badgeTypeId: achievementData?.badgeTypeId,
            }}
            setAchievementName={setAchievementName}
          />
        </Grid>
      </Grid>
      <Grid container className={classes.gridContainer}>
        <Grid className={classes.gridChild} item xs={12}>
          <Rules
            achievementData={{
              rules: achievementData?.rules,
            }}
          />
        </Grid>
      </Grid>
      <Grid container className={classes.gridContainer}>
        <Grid className={classes.gridChild} item xs={12}>
          <Limit
            achievementData={{
              limit: achievementData?.limit,
            }}
          />
        </Grid>
      </Grid>
      {editBadge.isOpen && (
        <BadgeNameChanger
          isOpen={editBadge.isOpen}
          badgeTypeId={editBadge.badgeTypeId}
          onClose={() =>
            setEditBadge({ isOpen: false, badgeTypeId: undefined })
          }
        />
      )}
    </>
  );
};
